import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  ViewEncapsulation,
  inject,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, interval, switchMap, from, takeWhile, filter } from 'rxjs';

import { IonicModule, ModalController } from '@ionic/angular';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Client } from 'src/providers/client/client';
import { AddAddressPage } from 'src/pages/add-address/add-address';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { MarketingChannelsService, AddressListing } from 'src/shared/services/marketing-channels.service';
import { ListingSetupService } from 'src/shared/services/listing-setup.service';
import { RentalUnitsService, RentalUnit, RentableSpace } from 'src/shared/services/rental-units.service';
import { ConnectStep, PLATFORM_MARK_LOGOS, getConnectFlow, getConnectIntro, getConnectOutro, getConnectSteps, getConnectTitle, getConnectingLabel, getContactPrompt, getListingPlatform, getPlatformIdLabel, isGatedPlatform, platformItemsByCategory, PlatformCategory, showConnectEmail } from 'src/shared/constants/listing-platforms';
import { describeChannelStatus, statusDotColor } from 'src/pages/property-listings/property-listings.status';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { AccountManagerService } from 'src/shared/services/account-manager.service';
import { OptimizerRunsService } from 'src/shared/services/optimizer-runs.service';
import { ListingOptimizerService } from 'src/shared/services/listing-optimizer.service';
import { OptimizerRun, OptimizerRunStatus } from 'src/models/optimizer-run.model';
import { OptimizationReport } from 'src/models/listing-optimizer.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { DateTime } from 'luxon';

// "What are you listing?" selector item — 0 means the entire property,
// any other value is a RentableSpace id. Labels are precomputed here so the
// template never calls a function (CLAUDE.md rule).
export interface ListingTargetOption {
  value: number;
  viewValue: string;
}

const ENTIRE_PROPERTY_TARGET = 0;
const ROOM_CATEGORY_ORDER: PlatformCategory[] = ['room_rental', 'mid_term', 'short_term', 'long_term'];

@Component({
  selector: 'channel-detail',
  templateUrl: './channel-detail.component.html',
  styleUrls: ['./channel-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    MatTooltipModule,
    TidyUiComponentsModule,
    SharedModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChannelDetailComponent implements OnInit {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private channelsService = inject(MarketingChannelsService);
  private rentalUnitsService = inject(RentalUnitsService);
  private optimizerRunsService = inject(OptimizerRunsService);
  private listingOptimizerService = inject(ListingOptimizerService);
  private featureGate = inject(FeatureGateService);
  private demoBlock = inject(DemoBlockService);
  private accountManager = inject(AccountManagerService);
  private modalCtrl = inject(ModalController);
  private listingSetup = inject(ListingSetupService);
  windowService = inject(WindowService);
  private readonly translatePipe = new TranslationPipe();

  state: 'loading' | 'add' | 'submitting' | 'pending' | 'syndicating' | 'detail' | 'report' | 'no-property' = 'loading';
  form: FormGroup;
  submitted = false;
  listing: AddressListing | null = null;
  addressItems: TidySelectNumberValueModel[] = [];
  addressName = '';
  addressCityState = '';
  fullAddress = '';
  // Precomputed presentation for the detail card (CLAUDE.md: no template functions).
  platformMark = '';
  platformAvatarClass = '';
  listingStatusDotColor = '';
  viewListingUrl: string | null = null;
  infoTooltip = '';
  // The connecting view: the platform's handover steps (icon stepper), how the
  // status reads, and the inline listing-ID editor with its Save button.
  connectSteps: ConnectStep[] = [];
  connectingLabel = '';
  pendingIdControl: FormControl<string>;
  pendingIdDirty = false;
  pendingIdSubmitted = false;
  isSavingPendingId = false;
  // Vrbo / Booking.com handovers run through the account manager: the connecting view
  // says who to send things to and offers call / email / text. Empty for Airbnb.
  contactPrompt = '';
  managerName = AccountManagerService.DEFAULT.name;
  managerPhoto = AccountManagerService.DEFAULT.photoUrl;
  private managerEmail = AccountManagerService.DEFAULT.email;
  private managerPhone = AccountManagerService.DEFAULT.phone;
  private addressId: number;
  private pollingSubscription: Subscription | null = null;

  private readonly defaultPlatformGroups = platformItemsByCategory();
  private readonly roomPlatformGroups = this.reorderGroupsForRoom(this.defaultPlatformGroups);
  platformGroups = this.defaultPlatformGroups;

  runs: OptimizerRun[] = [];
  runSuggestionCounts: Record<number, number | null> = {};
  readonly skeletonRuns = [0, 1, 2];
  runsLoading = false;
  runsError = false;
  private readonly RUNS_PER_PAGE = 5;

  // The run whose report is open in the 'report' state.
  selectedRun: OptimizerRun | null = null;
  selectedRunReport: OptimizationReport | null = null;
  selectedRunUnavailable = '';
  // True when the report was opened from this page's history list (back returns
  // to the listing detail); false when deep-linked from the Optimizer page
  // (no back — the user just closes/exits).
  private reportOpenedFromDetail = false;


  listingIdLabel = 'Airbnb Listing ID';

  // Overflow menu shown on the mobile top nav; the desktop panel gets the same
  // items through rightSidePanelService.setHeaderDropdownMenu().
  mobileDropdownMenu: { label: string; action: string }[] = [];

  // Room-aware "What are you listing?" selector — only populated (and shown)
  // when the selected property maps to a by-room unit, add-state only.
  private byRoomUnitsByAddress = new Map<number, RentalUnit>();
  selectedUnit: RentalUnit | null = null;
  listingTargetOptions: ListingTargetOption[] = [];
  selectedListingTarget = ENTIRE_PROPERTY_TARGET;
  private selectedSpace: RentableSpace | null = null;

  get selectedPlatform(): string {
    return this.listing?.platform || this.form?.value?.platform || 'airbnb';
  }

  get isCohostFlow(): boolean {
    return getConnectFlow(this.selectedPlatform) === 'cohost';
  }

  get isGatedPlatform(): boolean {
    return isGatedPlatform(this.selectedPlatform);
  }

  // Per-platform connect wording (Airbnb co-host invite / Vrbo relist / Booking.com ownership change).
  get platformConnectTitle(): string { return getConnectTitle(this.selectedPlatform); }
  get platformConnectIntro(): string { return getConnectIntro(this.selectedPlatform); }
  get platformConnectOutro(): string { return getConnectOutro(this.selectedPlatform); }
  get showCopyEmailLink(): boolean { return showConnectEmail(this.selectedPlatform); }

  async ngOnInit() {
    this.form = this.fb.group({
      address_id: [null as number, Validators.required],
      listing_target: [ENTIRE_PROPERTY_TARGET],
      platform: ['airbnb', Validators.required],
      listingId: ['', [Validators.required, this.airbnbListingIdValidator]]
    });

    this.form.get('platform')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(platform => {
      this.listingIdLabel = this.getLabelForPlatform(platform);
      this.form.get('listingId')!.updateValueAndValidity();
      this.cdr.markForCheck();
    });

    // Save only lights up once the ID differs from what's stored.
    this.pendingIdControl = this.fb.nonNullable.control('', [Validators.required, this.pendingIdValidator]);
    this.pendingIdControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.pendingIdDirty = (value || '').trim() !== (this.listing?.platform_id || '');
      this.cdr.markForCheck();
    });

    this.rightSidePanelService.headerIconClicked$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(iconId => {
      if (iconId === 'share-report') {
        this.shareReport();
      }
    });
    // Seeded with null and reset to null after each emit, so ignore the empty replays.
    this.rightSidePanelService.dropDownMenuAction$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(action => {
      if (action === 'deleteListing') {
        this.deleteListing();
      }
    });
    this.destroyRef.onDestroy(() => {
      this.rightSidePanelService.clearHeaderIcons();
      this.rightSidePanelService.clearHeaderDropdownMenu();
    });

    this.loadAccountManager();
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.listing = params?.listing || null;
    this.refreshListingPresentation();

    const addresses = await this.client.getAddresses();
    this.addressItems = this.client.parseAddressList(addresses);

    // Prefill from unit-detail's "List this room" — a real unitId/spaceId
    // takes priority over any addressId already on params.
    let prefillUnit: RentalUnit | undefined;
    if (params?.unitId) {
      prefillUnit = await this.rentalUnitsService.getUnit(params.unitId);
      if (prefillUnit?.address_id) {
        this.addressId = prefillUnit.address_id;
      } else if (params?.addressId) {
        this.addressId = params.addressId;
      }
    }

    this.rentalUnitsService.linkDemoUnitToFirstAddress(this.addressItems);
    const byRoomUnits = await this.rentalUnitsService.getByRoomUnits();
    this.byRoomUnitsByAddress = new Map(
      byRoomUnits.filter(u => !!u.address_id).map(u => [u.address_id as number, u])
    );

    // A prefilled unit may not be linked to a real address (only the demo
    // showcase unit is) — default the address so the form validates; the room
    // save path keys off unitId/spaceId, not the address.
    if (prefillUnit && !this.addressId) {
      this.addressId = this.addressItems.find(item => item.value > 0)?.value;
    }

    if (this.addressId) {
      this.form.patchValue({ address_id: this.addressId });
      const address = (addresses || []).find((a: any) => a.id === this.addressId);
      this.addressName = address?.address_name || address?.address1 || '';
      this.addressCityState = [address?.city, address?.state].filter(Boolean).join(', ');
      this.fullAddress = [
        [address?.address1, address?.address2].filter(Boolean).join(' '),
        address?.city,
        [address?.state, address?.zip].filter(Boolean).join(' ')
      ].filter(Boolean).join(', ');
    }
    this.rightSidePanelService.setDialogPageTitle(this.listing ? 'Listing Details' : 'Add Listing');
    // The opener's subtitle (e.g. the Listings modal's property line) would
    // otherwise linger in the dialog header — the address lives on the card here.
    this.rightSidePanelService.setDialogPageSubtitle(null);

    if (this.listing) {
      this.form.patchValue({
        platform: this.listing.platform,
        listingId: this.listing.platform_id || ''
      });
      this.listingIdLabel = this.getLabelForPlatform(this.listing.platform);

      if (this.listing.isMockRoomListing) {
        this.state = 'detail';
      } else if (this.listing.status !== 'connecting') {
        this.state = 'detail';
        this.loadRuns();
        // Deep link from the Optimizer page's report list: open the run's
        // report immediately, with no back affordance — closing exits.
        if (params?.run) {
          this.openRun(params.run as OptimizerRun, false);
        }
      } else if (this.isCohostFlow) {
        this.state = 'pending';
        this.startPolling();
      } else {
        this.state = 'syndicating';
      }
    } else if (this.addressItems.length === 0) {
      this.state = 'no-property';
    } else {
      this.applyAddressSelection(this.addressId, prefillUnit && params?.spaceId ? params.spaceId : null, prefillUnit);
      this.state = 'add';
    }
    this.publishDetailMenu();
    this.cdr.markForCheck();
  }

  // Rebuilds the room-target selector for the given address and applies the
  // preselection (used both on initial load and on address change). No-op
  // (beyond clearing selector state) when the address has no by-room unit —
  // accounts without room-splitting units must see byte-identical behavior.
  private applyAddressSelection(addressId: number | undefined, preselectSpaceId: number | null, unitOverride?: RentalUnit) {
    const unit = unitOverride || (addressId ? this.byRoomUnitsByAddress.get(addressId) : undefined);
    const hadRoomUnit = !!this.selectedUnit;
    this.selectedUnit = unit || null;

    if (!unit) {
      this.listingTargetOptions = [];
      this.selectedSpace = null;
      this.selectedListingTarget = ENTIRE_PROPERTY_TARGET;
      if (hadRoomUnit) {
        this.setRoomAware(false);
      }
      return;
    }

    const rooms = (unit.spaces || []).filter(s => s.type === 'room');
    this.listingTargetOptions = [
      { value: ENTIRE_PROPERTY_TARGET, viewValue: 'Entire property' },
      ...rooms.map(room => ({
        value: room.id,
        viewValue: `${room.name} · ${room.state || 'Vacant'}`
      }))
    ];

    const preselected = preselectSpaceId ? rooms.find(r => r.id === preselectSpaceId) : undefined;
    this.selectTarget(preselected ? preselected.id : ENTIRE_PROPERTY_TARGET);
  }

  // Applies the platform-group reorder + default-platform preselect for the
  // given listing target (0 = entire property, else a room's space id).
  private setRoomAware(isRoom: boolean) {
    this.platformGroups = isRoom ? this.roomPlatformGroups : this.defaultPlatformGroups;
    // Room listings syndicate from our side, so a platform listing ID is
    // optional there; entire-property listings keep requiring one.
    const listingIdControl = this.form.get('listingId')!;
    listingIdControl.setValidators(isRoom
      ? [this.airbnbListingIdValidator]
      : [Validators.required, this.airbnbListingIdValidator]);
    listingIdControl.updateValueAndValidity();
    this.form.get('platform')!.setValue(isRoom ? 'padsplit' : 'airbnb');
  }

  // Room categories first (room_rental, then mid_term), short/long term after —
  // everything else keeps its relative order from the default grouping.
  private reorderGroupsForRoom<T extends { category: PlatformCategory }>(groups: T[]): T[] {
    const rank = (category: PlatformCategory) => {
      const index = ROOM_CATEGORY_ORDER.indexOf(category);
      return index === -1 ? ROOM_CATEGORY_ORDER.length : index;
    };
    return [...groups].sort((a, b) => rank(a.category) - rank(b.category));
  }

  private getLabelForPlatform(platform: string): string {
    return getPlatformIdLabel(platform);
  }

  goToAddProperty = () => {
    const params = { hasNoAddresses: true };
    if (this.windowService.isDesktopRes) {
      // Already in a secondary-layer panel — navigate within it, don't drop to base nav.
      this.rightSidePanelService.navigateTo('add-property', params, AddAddressPage);
    } else {
      this.navCtrl.navigateForward('add-property', params);
    }
  };

  onAddressChange = (event: any) => {
    this.addressId = event?.value ?? event;
    this.applyAddressSelection(this.addressId, null);
    this.cdr.markForCheck();
  };

  selectPlatform = (platform: string) => {
    this.form.get('platform')!.setValue(platform);
  };

  selectTarget = (target: number) => {
    this.selectedListingTarget = target;
    this.form.get('listing_target')!.setValue(target);
    const rooms = (this.selectedUnit?.spaces || []).filter(s => s.type === 'room');
    this.selectedSpace = target === ENTIRE_PROPERTY_TARGET ? null : (rooms.find(r => r.id === target) || null);
    this.setRoomAware(!!this.selectedSpace);
    this.cdr.markForCheck();
  };

  addListing = async () => {
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }
    // Long-term / mid-term / by-the-room syndication ships in the UI ahead of the
    // backend that actually posts these listings, so gate the write and point the
    // user at their account manager instead of failing silently downstream.
    // Room targets are gated regardless of platform — a room listing on any
    // channel is still a mock write with no backend behind it.
    if ((this.isGatedPlatform || this.selectedSpace) && this.featureGate.gate()) {
      this.submitted = false;
      this.state = 'add';
      this.cdr.markForCheck();
      return;
    }
    this.state = 'submitting';
    this.cdr.markForCheck();
    try {
      const platform = this.form.value.platform;
      const rawListingId = this.form.value.listingId || '';
      const listingId = platform === 'airbnb'
        ? this.channelsService.parseAirbnbListingId(rawListingId)!
        : rawListingId.trim();

      if (this.selectedUnit && this.selectedSpace) {
        await this.rentalUnitsService.addRoomListing(this.selectedUnit.id, this.selectedSpace.id, {
          platform,
          platform_id: listingId || undefined
        });
        this.listing = null;
        this.submitted = false;
        this.state = 'syndicating';
        this.cdr.markForCheck();
        return;
      }

      const data: any = {
        address_id: this.addressId,
        platform
      };
      if (listingId) {
        data.platform_id = listingId;
      }

      if (this.listing) {
        const updated: any = await this.client.updateAddressListing(this.listing.id, {
          platform_id: listingId
        });
        this.listing = { ...this.listing, ...updated };
      } else {
        this.listing = await this.client.createAddressListing(data) as any;
        void this.listingSetup.addListingConnectItem({
          id: this.listing?.id,
          platform,
          addressName: this.addressItems.find((item) => item.value === this.addressId)?.viewValue || this.addressName,
        });
      }
      this.refreshListingPresentation();
      this.submitted = false;
      this.cdr.markForCheck();
      if (this.isCohostFlow) {
        this.state = 'pending';
        this.startPolling();
      } else {
        this.state = 'syndicating';
      }
    } catch (err: any) {
      this.state = this.listing ? (this.isCohostFlow ? 'pending' : 'syndicating') : 'add';
      this.cdr.markForCheck();
      this.util.showError(err?.error?.message || err?.message || 'Failed to add listing. Please try again.');
    }
  };

  // Inline edit of the listing ID on the connecting view (replaces the old "Edit
  // Listing" hop back to the add form). Polling keys off the listing's own id, so a
  // changed platform id doesn't disturb it.
  savePendingListingId = async () => {
    if (!this.listing || this.isSavingPendingId) {return;}
    this.pendingIdSubmitted = true;
    const platformId = this.channelsService.parseListingId(this.pendingIdControl.value, this.listing.platform);
    if (!platformId) {
      this.cdr.markForCheck();
      return;
    }
    if (this.demoBlock.block()) {return;}
    this.isSavingPendingId = true;
    this.cdr.markForCheck();
    try {
      const updated: any = await this.client.updateAddressListing(this.listing.id, { platform_id: platformId });
      this.listing = { ...this.listing, ...(updated || {}), platform_id: platformId };
      this.form.patchValue({ listingId: platformId }, { emitEvent: false });
      this.refreshListingPresentation();
      this.pendingIdSubmitted = false;
      this.util.showSuccess('Listing ID saved.');
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Could not save the listing ID. Please try again.');
    } finally {
      this.isSavingPendingId = false;
      this.cdr.markForCheck();
    }
  };

  get pendingIdError(): string {
    if (this.pendingIdControl?.hasError('invalidListing')) {
      return this.channelsService.invalidListingIdMessage(this.selectedPlatform);
    }
    return 'Please enter your listing ID';
  }

  private pendingIdValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value || '').toString().trim();
    if (!value || !this.listing) {return null;}
    return this.channelsService.parseListingId(value, this.listing.platform) === null
      ? { invalidListing: true }
      : null;
  };

  copyEmail = () => {
    navigator.clipboard.writeText('pm@tidy.com');
    this.util.showSuccess('Email copied to clipboard.');
  };

  private async loadAccountManager(): Promise<void> {
    try {
      const manager = await this.accountManager.getDisplay();
      this.managerName = manager.name;
      this.managerPhoto = manager.photoUrl;
      this.managerEmail = manager.email;
      this.managerPhone = manager.phone;
      this.cdr.markForCheck();
    } catch {
      // Keep TIDY's defaults.
    }
  }

  callManager(): void {
    window.location.href = `tel:${this.managerPhone}`;
  }

  emailManager(): void {
    window.location.href = `mailto:${this.managerEmail}`;
  }

  textManager(): void {
    window.location.href = `sms:${this.managerPhone}`;
  }

  get airbnbCohostUrl(): string | null {
    if (this.selectedPlatform !== 'airbnb') {return null;}
    const raw = this.listing?.platform_id || this.form?.value?.listingId || '';
    const id = this.channelsService.parseAirbnbListingId(raw);
    return id ? `https://www.airbnb.com/hosting/listings/editor/${id}/details/co-hosts` : null;
  }

  get listingIdErrorMessage(): string {
    const control = this.form?.get('listingId');
    if (control?.hasError('invalidAirbnbListing')) {
      return 'Enter a numeric Airbnb listing ID or a listing URL (e.g. airbnb.com/rooms/12345678).';
    }
    return 'Please enter your listing ID';
  }

  openCohostsPage = () => {
    if (this.airbnbCohostUrl) {
      this.util.openUrl(this.airbnbCohostUrl);
    }
  };

  private airbnbListingIdValidator = (control: AbstractControl): ValidationErrors | null => {
    const platform = this.form?.get('platform')?.value;
    if (platform !== 'airbnb') {return null;}
    const value = (control.value || '').trim();
    if (!value) {return null;}
    return this.channelsService.parseAirbnbListingId(value)
      ? null
      : { invalidAirbnbListing: true };
  };

  private startPolling() {
    this.pollingSubscription?.unsubscribe();
    if (!this.listing) {return;}
    this.pollingSubscription = interval(5000).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => from(this.client.getAddressListings(this.addressId))),
      takeWhile((listings: any[]) => {
        const current = listings?.find(l => l.id === this.listing!.id);
        return current?.status === 'connecting';
      }, true),
      filter((listings: any[]) => {
        const current = listings?.find(l => l.id === this.listing!.id);
        return current?.status !== 'connecting';
      })
    ).subscribe({
      next: (listings: any[]) => {
        const current = listings.find(l => l.id === this.listing!.id);
        this.listing = { ...this.listing!, ...current };
        this.refreshListingPresentation();
        this.state = 'detail';
        this.publishDetailMenu();
        this.cdr.markForCheck();
        this.loadRuns();
      },
      error: () => {
        // Swallow errors during listing refresh
      }
    });
  }

  // The avatar mark, status dot colour, and public listing URL for the detail card.
  private refreshListingPresentation(): void {
    const listing = this.listing;
    if (!listing) {
      this.platformMark = '';
      this.platformAvatarClass = '';
      this.listingStatusDotColor = '';
      this.viewListingUrl = null;
      this.connectSteps = [];
      this.connectingLabel = '';
      this.contactPrompt = '';
      return;
    }
    const isBranded = !!PLATFORM_MARK_LOGOS[listing.platform];
    this.platformMark = PLATFORM_MARK_LOGOS[listing.platform] || getListingPlatform(listing.platform)?.logo || '';
    this.platformAvatarClass = isBranded ? listing.platform : '';
    this.listingStatusDotColor = statusDotColor(listing.status);
    this.viewListingUrl = this.publicListingUrl(listing);
    this.connectSteps = getConnectSteps(listing.platform);
    this.connectingLabel = getConnectingLabel(listing.platform);
    this.contactPrompt = getContactPrompt(listing.platform);
    this.pendingIdControl?.setValue(listing.platform_id || '', { emitEvent: false });
    this.pendingIdDirty = false;
    // The info hint's hover: connected date + what the status means, one per line.
    const t = (text: string) => this.translatePipe.transform(text);
    this.infoTooltip = [
      `${t('Connected: ')}${this.formatDate(listing.created_at)}`,
      t(describeChannelStatus(listing.status))
    ].filter(Boolean).join('\n');
  }

  // The listing's public page. Only Airbnb and VRBO URLs are derivable from the
  // listing ID; Booking.com property pages are slug-based, so no link there.
  private publicListingUrl(listing: AddressListing): string | null {
    if (!listing.platform_id) {return null;}
    switch (listing.platform) {
      case 'airbnb': return `https://www.airbnb.com/rooms/${listing.platform_id}`;
      case 'vrbo': return `https://www.vrbo.com/${listing.platform_id}`;
      default: return null;
    }
  }

  // Opens the Listings editor for this property — edits there apply to all of
  // the property's connected listings. No component import: property-listings.page
  // imports this component for its own navigation, so importing it back (even
  // dynamically — it reshuffles the chunk graph) is a module cycle; navigateTo
  // resolves the component from the route registry instead. And no dialog-params
  // overwrite: navigateTo merges params over the stored ones, keeping `listing`
  // so panel-back re-lands on this detail view rather than the add form.
  goToEditListing = () => {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Listings');
      this.rightSidePanelService.navigateTo('property-listings', { addressId: this.addressId });
    } else {
      this.navCtrl.navigateForward('property-listings');
    }
  };

  openViewListing = () => {
    if (this.viewListingUrl) {
      this.util.openUrl(this.viewListingUrl);
    }
  };

  formatPlatform(platform: string): string {
    return this.channelsService.formatPlatform(platform);
  }

  formatStatus(status: string): string {
    if (!status) {return 'Unknown';}
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) {return '-';}
    return DateTime.fromISO(dateStr).toFormat('MMM d, yyyy');
  }

  // Released reports only — the backend returns just success + concierge-released
  // runs to the client, each already carrying its `done` suggestions (the
  // concierge-approved "what was done"), so there's nothing to poll or hydrate.
  private loadRuns() {
    // Only Airbnb listings can be optimized, and the history section is hidden
    // for every other platform — so there's nothing to fetch.
    if (!this.listing || this.listing.platform !== 'airbnb') {return;}
    this.runsLoading = true;
    this.runsError = false;
    this.cdr.markForCheck();
    this.optimizerRunsService.list(this.listing.id, { per_page: this.RUNS_PER_PAGE, includes: 'done_suggestions' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runs) => {
          this.setRuns(runs);
          this.runsLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.runsError = true;
          this.runsLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private setRuns(runs: OptimizerRun[]): void {
    // Order by when the optimization actually ran (not when concierge released
    // it), newest first — releasing a backlog stamps many runs with the same
    // reviewed_at, so that date can't drive the client's chronology.
    this.runs = [...(runs || [])].sort((a, b) => this.runGeneratedMs(b) - this.runGeneratedMs(a));
    this.runSuggestionCounts = Object.fromEntries(
      this.runs.map(run => [run.id, this.countRunSuggestions(run)])
    );
  }

  private runGeneratedMs(run: OptimizerRun): number {
    const date = run.started_at || run.created_at;
    const ms = date ? DateTime.fromISO(date).toMillis() : NaN;
    return Number.isFinite(ms) ? ms : 0;
  }

  private countRunSuggestions(run: OptimizerRun): number | null {
    const done = run.done_suggestions;
    if (!Array.isArray(done)) {return null;}
    return done.length;
  }

  // ---------- Report view (one run, read-only "what was done") ----------

  openRun = (run: OptimizerRun, fromDetail = true) => {
    this.selectedRun = run;
    this.selectedRunReport = this.buildDoneReport(run);
    this.selectedRunUnavailable = this.selectedRunReport ? '' : 'No changes were applied in this optimization.';
    this.state = 'report';
    this.reportOpenedFromDetail = fromDetail;
    this.publishDetailMenu();
    this.rightSidePanelService.setDialogPageTitle('Optimization Report');
    this.rightSidePanelService.customBackAction$.next(fromDetail ? () => this.backToDetail() : null);
    this.updateShareHeaderIcon();
    this.cdr.markForCheck();
  };

  // A report projection carrying only the concierge-approved (`done`) changes.
  // The report component reads `suggestions` + `listing.photos`; before/after
  // photo URLs already ride each suggestion payload, so an empty gallery is fine.
  private buildDoneReport(run: OptimizerRun): OptimizationReport | null {
    const suggestions = (run.done_suggestions || [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(entry => entry.payload)
      .filter(Boolean);
    if (suggestions.length === 0) {return null;}
    return { suggestions, listing: { photos: [] } } as unknown as OptimizationReport;
  }

  backToDetail = () => {
    this.state = 'detail';
    this.selectedRun = null;
    this.selectedRunReport = null;
    this.selectedRunUnavailable = '';
    this.rightSidePanelService.setDialogPageTitle('Listing Details');
    this.rightSidePanelService.customBackAction$.next(null);
    this.rightSidePanelService.clearHeaderIcons();
    this.publishDetailMenu();
    this.cdr.markForCheck();
  };

  // Mobile top-nav back: when the report was opened from this page's history,
  // back leaves the report view without leaving the page. Deep-linked reports
  // fall through to the normal page back (exit).
  topNavBack = (): boolean => {
    if (this.state === 'report' && this.reportOpenedFromDetail) {
      this.backToDetail();
      return false;
    }
    return true;
  };

  shareReport = (): void => {
    this.util.copyToClipBoard(this.listingOptimizerService.buildShareLink(this.selectedRunReport));
  };

  // The overflow menu only makes sense on a real, saved listing sitting in the
  // detail view — mock room listings have synthetic ids with no backend row, and
  // the report view has its own header actions.
  private publishDetailMenu(): void {
    const canDelete = this.state === 'detail' && !!this.listing?.id && !this.listing.isMockRoomListing;
    if (!canDelete) {
      this.mobileDropdownMenu = [];
      this.rightSidePanelService.clearHeaderDropdownMenu();
      return;
    }
    const items = [{ label: this.translatePipe.transform('Disconnect Listing'), action: 'deleteListing' }];
    if (this.windowService.isDesktopRes) {
      this.mobileDropdownMenu = [];
      this.rightSidePanelService.setHeaderDropdownMenu(items);
    } else {
      this.mobileDropdownMenu = items;
      this.rightSidePanelService.clearHeaderDropdownMenu();
    }
  }

  deleteListing = async () => {
    // DELETE isn't in the demo interceptor's blocked-verb set, so this guard is
    // what keeps the shared demo account from losing a listing for everyone.
    if (this.demoBlock.block()) {return;}
    if (!this.listing?.id || this.listing.isMockRoomListing) {return;}
    const params: ConfirmPageParamsModel = {
      title: 'Disconnect Listing?',
      body: `This removes the connection between TIDY and this ${this.formatPlatform(this.listing.platform)} listing. Your listing on ${this.formatPlatform(this.listing.platform)} is not affected, and TIDY will stop syncing its reservations.`,
      backText: 'Go Back',
      confirmText: 'Disconnect Listing',
      confirmAction: this.confirmDeleteListing
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  };

  // Thrown errors are caught by ConfirmPage and shown inline, so only dismiss on
  // success — a failed delete keeps the modal open with the reason.
  private confirmDeleteListing = async () => {
    await this.client.deleteAddressListing(this.listing!.id);
    this.util.showSuccess('Listing disconnected.');
    await this.modalCtrl.dismiss();
    if (this.windowService.isDesktopRes) {
      // Closing the panel is itself the channels table's refresh trigger.
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.channelsService.listingsChanged$.next();
      this.navCtrl.navigateBack('revenue');
    }
  };

  private updateShareHeaderIcon(): void {
    if (!this.windowService.isDesktopRes) {return;}
    if (this.state === 'report' && this.selectedRunReport) {
      this.rightSidePanelService.setHeaderIcons([
        { id: 'share-report', icon: 'assets/svg/share-white.svg', tooltip: 'Share report' }
      ]);
    } else {
      this.rightSidePanelService.clearHeaderIcons();
    }
  }

  formatRunStatus(status: OptimizerRunStatus): string {
    const map: Record<OptimizerRunStatus, string> = {
      running: 'Running',
      success: 'Success',
      failure: 'Failed',
    };
    return map[status] ?? status;
  }

  getRunStatusClass(status: OptimizerRunStatus): string {
    return `status-${status}`;
  }

  formatRunDate(dateStr: string | null): string {
    if (!dateStr) {return '—';}
    return DateTime.fromISO(dateStr).toFormat('MMM d, yyyy \'at\' h:mm a');
  }
}
